import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { UserPlus, Users, Loader2, CheckCircle2, Plus } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { StatCard } from '@/components/shared/stat-card'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { currentLocale } from '@/lib/i18n'
import { CreateFriendCampaignDialog } from './create-friend-campaign-dialog'
import { useMyPermissions } from '@/hooks/use-settings'
import {
  useChienDichKetBan,
  tyLeChapNhan,
  NHAN_TRANG_THAI,
  type ChienDichKetBan,
  type TrangThaiChienDich,
} from '@/hooks/use-zalo-friend-campaigns'

const MAU: Record<TrangThaiChienDich, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  draft: 'outline',
  running: 'default',
  paused: 'secondary',
  completed: 'secondary',
  cancelled: 'destructive',
}

export function FriendCampaignsPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('marketing')
  const { data: rows = [], isLoading } = useChienDichKetBan()
  const [moTao, setMoTao] = useState(false)
  // Hộp tạo gọi POST tạo (friend_campaigns.create) rồi nạp tệp (…/targets/import → friend_campaigns.update)
  // → thiếu một trong hai là ẩn nút, tránh để lại chiến dịch nháp rỗng (BRD §5.3.1).
  const { data: perms } = useMyPermissions()
  const coQuyenTao = !!perms?.has('friend_campaigns.create') && !!perms?.has('friend_campaigns.update')

  const tong = useMemo(() => ({
    chienDich: rows.length,
    dangChay: rows.filter((c) => c.status === 'running').length,
    daMoi: rows.reduce((s, c) => s + c.invitedCount, 0),
    daDongY: rows.reduce((s, c) => s + c.acceptedCount, 0),
  }), [rows])

  const cot: Column<ChienDichKetBan>[] = [
    {
      key: 'name',
      header: t('friendCampaigns.columns.name'),
      cell: (r) => (
        <div>
          <div className="font-medium">{r.name}</div>
          <div className="text-xs text-muted-foreground">
            {t('friendCampaigns.accountsCreated', { count: r.accounts.length, date: dayjs(r.createdAt).format('DD/MM/YYYY') })}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('friendCampaigns.columns.status'),
      cell: (r) => <Badge variant={MAU[r.status]}>{NHAN_TRANG_THAI[r.status]}</Badge>,
    },
    {
      key: 'tien',
      header: t('friendCampaigns.columns.invitedTotal'),
      align: 'right',
      cell: (r) => (
        <span className="tabular-nums">
          {r.invitedCount.toLocaleString(currentLocale())} / {r.totalCount.toLocaleString(currentLocale())}
        </span>
      ),
    },
    {
      key: 'dongY',
      header: t('friendCampaigns.columns.accepted'),
      align: 'right',
      cell: (r) => <span className="tabular-nums">{r.acceptedCount.toLocaleString(currentLocale())}</span>,
    },
    {
      key: 'tyLe',
      header: t('friendCampaigns.columns.acceptRate'),
      align: 'right',
      cell: (r) => {
        const rate = tyLeChapNhan(r)
        if (r.invitedCount === 0) return <span className="text-muted-foreground">—</span>
        // Dưới 26% thì hết tệp trước khi tài khoản đầy — xem mục B4 báo cáo tiến độ.
        const canh = rate < 0.26
        return (
          <span className={canh ? 'font-medium text-amber-600 tabular-nums' : 'tabular-nums'}>
            {(rate * 100).toFixed(1)}%
          </span>
        )
      },
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('friendCampaigns.title')}
        description={t('friendCampaigns.description')}
        actions={
          coQuyenTao && (
            <Button onClick={() => setMoTao(true)}>
              <Plus className="h-4 w-4" /> {t('friendCampaigns.create')}
            </Button>
          )
        }
      />

      {coQuyenTao && <CreateFriendCampaignDialog open={moTao} onOpenChange={setMoTao} />}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label={t('friendCampaigns.stats.total')} value={tong.chienDich} icon={UserPlus} />
        <StatCard label={t('friendCampaigns.stats.running')} value={tong.dangChay} icon={Loader2} tone="warning" />
        <StatCard label={t('friendCampaigns.stats.invited')} value={tong.daMoi} icon={Users} tone="primary" />
        <StatCard label={t('friendCampaigns.stats.accepted')} value={tong.daDongY} icon={CheckCircle2} tone="success" />
      </div>

      <DataTable
        columns={cot}
        rows={rows}
        loading={isLoading}
        rowKey={(r) => r.id}
        emptyTitle={t('friendCampaigns.empty')}
        onRowClick={(r) => navigate(`/zalo-friend-campaigns/${r.id}`)}
      />
    </div>
  )
}
